/**
 * ffmpegService.js — 浏览器端 FFmpeg.wasm 视频/音频裁剪服务
 *
 * 核心思路：
 *   将音视频裁剪从服务器迁移到用户浏览器执行，
 *   服务器只负责存储裁剪结果，不再承担 CPU 密集的编码工作。
 *
 * 技术要点：
 *   - 使用 @ffmpeg/ffmpeg 0.12.x（WebAssembly 版 FFmpeg）
 *   - 单线程模式（UMD core），无需 COOP/COEP 响应头
 *   - 核心文件从 CDN 懒加载（首次约 31MB，浏览器缓存后秒开）
 *   - 默认 stream copy（-c copy）零重编码，速度极快（< 1秒）
 *   - 空闲 5 分钟自动释放 WASM 实例，节省 ~60MB 内存
 *
 * 导出：
 *   loadFFmpeg(onProgress)     — 加载/获取 FFmpeg 实例
 *   clipVideo(blob, start, end, onProgress) → Blob
 *   clipAudio(blobOrUrl, start, end, onProgress) → Blob
 *   extractAudioFromVideo(blobUrl, onProgress) → Blob  — 从视频中提取完整音频轨
 *   isFFmpegSupported()        — 检测浏览器是否支持
 *   CLIENT_CLIP_MAX_SIZE       — 客户端裁剪的文件大小上限
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import localCacheService from './localCacheService';

// ── 配置 ─────────────────────────────────────────────────────────────────────

/** 站点内托管的 FFmpeg core 地址（优先） */
const LOCAL_CORE_BASE_URL = `${window.location.origin}/ffmpeg-core`;

/** OSS 加速地址（国内用户优先，比香港站点快） */
const OSS_CORE_BASE_URL = 'https://cineart-canvas.oss-cn-beijing.aliyuncs.com/static/ffmpeg-core';

/** CDN 基地址（兜底回退） */
const CDN_CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

/** 客户端裁剪的文件大小上限（超过此大小降级为服务器处理） */
export const CLIENT_CLIP_MAX_SIZE = 500 * 1024 * 1024; // 500MB

// ── 单例管理 ─────────────────────────────────────────────────────────────────

let ffmpeg = null;           // FFmpeg 实例（全局单例）
let loadPromise = null;      // 防止重复加载
let idleTimer = null;        // 空闲自动释放定时器

/** 空闲超时时间（5 分钟）：FFmpeg.wasm 占 ~60MB 内存，长时间不用应释放 */
const IDLE_TIMEOUT = 5 * 60 * 1000;

function normalizeBaseUrl(baseUrl) {
  return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
}

function buildLoadProgressHandler(onLoadProgress) {
  if (!onLoadProgress) return undefined;
  return (event) => {
    if (event.total > 0) {
      onLoadProgress(Math.round((event.received / event.total) * 100));
    }
  };
}

async function loadCoreFromBase(baseUrl, onLoadProgress) {
  const normalizedBase = normalizeBaseUrl(baseUrl);
  const coreURL = await toBlobURL(`${normalizedBase}ffmpeg-core.js`, 'text/javascript');
  const wasmURL = await toBlobURL(
    `${normalizedBase}ffmpeg-core.wasm`,
    'application/wasm',
    true,
    buildLoadProgressHandler(onLoadProgress)
  );
  return { coreURL, wasmURL };
}

/**
 * 重置空闲计时器 — 每次使用 FFmpeg 时调用
 * 5 分钟内无操作自动释放 WASM 实例，下次使用时重新加载（浏览器有缓存，很快）
 */
function resetIdleTimer() {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    if (ffmpeg) {
      try { ffmpeg.terminate(); } catch {}
      ffmpeg = null;
      loadPromise = null;
      console.log('[ffmpegService] 空闲 5 分钟，已释放 FFmpeg.wasm 实例');
    }
  }, IDLE_TIMEOUT);
}

/**
 * 检测浏览器是否支持 FFmpeg.wasm
 * 要求：WebAssembly + SharedArrayBuffer 或至少基本 WASM 支持
 */
export function isFFmpegSupported() {
  return typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function';
}

/**
 * 加载 FFmpeg.wasm 实例（懒加载，站点优先，CDN 兜底）
 *
 * @param {(p: number) => void} [onLoadProgress] - 加载进度回调（0-100）
 * @returns {Promise<FFmpeg>} 已加载的 FFmpeg 实例
 */
export async function loadFFmpeg(onLoadProgress) {
  // 已加载，直接返回
  if (ffmpeg) return ffmpeg;

  // 正在加载，等待完成
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const ff = new FFmpeg();
    const ossBase = normalizeBaseUrl(OSS_CORE_BASE_URL);
    const localBase = normalizeBaseUrl(LOCAL_CORE_BASE_URL);
    const cdnBase = normalizeBaseUrl(CDN_CORE_BASE_URL);

    // 先尝试用浏览器缓存回填，减少二次下载。
    await localCacheService.prepareFfmpegAssets(localBase).catch(() => {});

    let loadedFrom = 'oss';
    let urls;

    try {
      // OSS 北京优先（国内用户 23MB/s，比香港站点快 100 倍）
      urls = await loadCoreFromBase(ossBase, onLoadProgress);
      await ff.load(urls);
    } catch (ossErr) {
      // OSS 不可用 → 尝试站点本地（浏览器缓存命中时也很快）
      try {
        loadedFrom = 'local';
        console.warn('[ffmpegService] OSS core 加载失败，尝试站点本地:', ossErr?.message || ossErr);
        urls = await loadCoreFromBase(localBase, onLoadProgress);
        await ff.load(urls);
      } catch (localErr) {
        // 站点也失败 → 回退官方 CDN
        loadedFrom = 'cdn';
        console.warn('[ffmpegService] 站点 core 加载失败，回退 CDN:', localErr?.message || localErr);
        urls = await loadCoreFromBase(cdnBase, onLoadProgress);
        await ff.load(urls);
      }
    }

    ffmpeg = ff;
    // 把浏览器缓存中的 FFmpeg 资源回写到本地文件夹。
    localCacheService.syncFfmpegAssetsFromBrowserCache(localBase).catch(() => {});
    if (loadedFrom !== 'local') {
      console.log(`[ffmpegService] 本次使用 ${loadedFrom} 回退完成加载`);
    }
    resetIdleTimer();  // 加载完成，启动空闲计时
    return ff;
  })();

  try {
    return await loadPromise;
  } catch (err) {
    // 加载失败，重置状态允许重试
    loadPromise = null;
    ffmpeg = null;
    throw err;
  }
}

// ── 视频裁剪 ─────────────────────────────────────────────────────────────────

/**
 * 在浏览器端裁剪视频（libx264 重编码，帧精确）
 *
 * 使用 -c:v libx264 重编码保证帧精确裁剪。
 * 短视频（5-10 秒）WASM 重编码约需 5-15 秒，用户可接受。
 *
 * @param {string} blobUrl     - 视频的 blob:// URL（来自本地缓存或 previewUrl）
 * @param {number} startTime   - 裁剪起点（秒）
 * @param {number} endTime     - 裁剪终点（秒）
 * @param {(p: number) => void} [onProgress] - 处理进度（0-100）
 * @returns {Promise<Blob>} 裁剪后的视频 Blob（video/mp4）
 */
export async function clipVideo(blobUrl, startTime, endTime, onProgress) {
  const ff = await loadFFmpeg();
  const duration = endTime - startTime;

  // === 注册进度监听 ===
  let progressHandler;
  if (onProgress) {
    progressHandler = ({ progress }) => {
      onProgress(Math.min(99, Math.round(progress * 100)));
    };
    ff.on('progress', progressHandler);
  }

  try {
    // 1. 将视频写入 FFmpeg 虚拟文件系统
    const inputData = await fetchFile(blobUrl);
    await ff.writeFile('input.mp4', inputData);

    // 2. 执行裁剪（libx264 重编码，帧精确）
    //    -ss 在 -i 之前 → 输入侧快速 seek
    //    -c:v libx264 → 重编码保证帧精确裁剪（-c copy 会跳到关键帧，导致裁剪不准）
    //    -crf 18 → 高画质（视觉无损）
    //    -preset fast → 编码速度与质量平衡
    //    -avoid_negative_ts → 修正时间戳
    //    -movflags +faststart → MP4 moov atom 前置（流式播放友好）
    await ff.exec([
      '-ss', startTime.toFixed(4),
      '-i', 'input.mp4',
      '-t', duration.toFixed(4),
      '-c:v', 'libx264',
      '-preset', 'fast',
      '-crf', '18',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-avoid_negative_ts', 'make_zero',
      '-movflags', '+faststart',
      '-y',
      'output.mp4'
    ]);

    // 3. 读取输出文件
    const outputData = await ff.readFile('output.mp4');

    // 4. 清理虚拟文件系统
    try { await ff.deleteFile('input.mp4'); } catch {}
    try { await ff.deleteFile('output.mp4'); } catch {}

    if (onProgress) onProgress(100);
    resetIdleTimer();  // 裁剪完成，重置空闲计时

    return new Blob([outputData.buffer], { type: 'video/mp4' });
  } finally {
    // 移除进度监听，防止下次裁剪重复触发
    if (progressHandler) {
      ff.off('progress', progressHandler);
    }
  }
}

// ── 音频裁剪 ─────────────────────────────────────────────────────────────────

/**
 * MIME type → FFmpeg 可识别的文件扩展名
 * FFmpeg.wasm 依赖文件扩展名判断输入格式，必须使用正确扩展名
 */
function mimeToAudioExt(mime) {
  if (!mime) return '.mp3';
  const m = mime.toLowerCase();
  if (m.includes('mpeg') || m.includes('mp3')) return '.mp3';
  if (m.includes('mp4') || m.includes('m4a') || m.includes('aac')) return '.m4a';
  if (m.includes('wav'))  return '.wav';
  if (m.includes('ogg'))  return '.ogg';
  if (m.includes('webm')) return '.webm';
  if (m.includes('flac')) return '.flac';
  return '.mp3'; // 兜底
}

/**
 * 在浏览器端裁剪音频（统一输出为 MP3 流式格式）
 *
 * 使用 MP3 输出而非 stream copy M4A 的原因：
 * 1. MP3 是流式格式，天然支持 HTTP range request（拖动进度条）
 * 2. M4A 即使加 -movflags +faststart，某些编码组合仍会出现 ERR_REQUEST_RANGE_NOT_SATISFIABLE
 * 3. 音频重编码速度极快（<1秒），用户无感
 *
 * @param {Blob|string} source  - 音频 Blob 或 blob:// URL
 * @param {number} startTime    - 裁剪起点（秒）
 * @param {number} endTime      - 裁剪终点（秒）
 * @param {(p: number) => void} [onProgress] - 处理进度（0-100）
 * @returns {Promise<Blob>} 裁剪后的音频 Blob（audio/mpeg）
 */
export async function clipAudio(source, startTime, endTime, onProgress) {
  const ff = await loadFFmpeg();
  const duration = endTime - startTime;

  // 🛡️ 防御性检查：duration 必须大于 0.1 秒
  if (duration < 0.1) {
    throw new Error(`裁剪时长过短：${duration.toFixed(4)}秒（需要 ≥0.1秒）。起点=${startTime.toFixed(4)}，终点=${endTime.toFixed(4)}`);
  }

  console.log(`[clipAudio] 裁剪参数: start=${startTime.toFixed(4)}s, end=${endTime.toFixed(4)}s, duration=${duration.toFixed(4)}s`);

  let progressHandler;
  if (onProgress) {
    progressHandler = ({ progress }) => {
      onProgress(Math.min(99, Math.round(progress * 100)));
    };
    ff.on('progress', progressHandler);
  }

  try {
    // 根据来源类型获取数据和正确的文件扩展名
    let inputData;
    let ext;
    if (source instanceof Blob) {
      inputData = new Uint8Array(await source.arrayBuffer());
      ext = mimeToAudioExt(source.type);
    } else {
      inputData = await fetchFile(source);
      ext = '.mp3'; // URL 模式兜底
    }

    const inputName = `input${ext}`;
    await ff.writeFile(inputName, inputData);

    // 🎵 统一输出为 MP3 流式格式（确保 range request 支持）
    const outputName = 'output.mp3';
    const outputMime = 'audio/mpeg';

    // 音频裁剪并转码为 MP3
    await ff.exec([
      '-ss', startTime.toFixed(4),
      '-i', inputName,
      '-t', duration.toFixed(4),
      '-vn',                    // 禁用视频流
      '-codec:a', 'libmp3lame', // 使用 LAME MP3 编码器
      '-b:a', '192k',           // 192kbps 比特率（音质与文件大小平衡）
      '-ar', '44100',           // 采样率 44.1kHz
      '-avoid_negative_ts', 'make_zero',
      '-y',
      outputName
    ]);

    const outputData = await ff.readFile(outputName);

    try { await ff.deleteFile(inputName); } catch {}
    try { await ff.deleteFile(outputName); } catch {}

    if (onProgress) onProgress(100);
    resetIdleTimer();  // 裁剪完成，重置空闲计时

    return new Blob([outputData.buffer], { type: outputMime });
  } finally {
    if (progressHandler) {
      ff.off('progress', progressHandler);
    }
  }
}

// ── 从视频提取音频 ─────────────────────────────────────────────────────────────

/**
 * 从视频中提取完整音频轨（浏览器端，零上传）
 *
 * 使用 -vn -acodec copy 直接复制音频流，不重编码，速度极快。
 * 输出 M4A 容器（AAC 音频流直接 copy，无损无延迟）。
 *
 * @param {string} blobUrl      - 视频的 blob:// 或 http URL
 * @param {(p: number) => void} [onProgress] - 处理进度（0-100）
 * @returns {Promise<Blob>} 提取出的音频 Blob（audio/mp4）
 */
export async function extractAudioFromVideo(blobUrl, onProgress) {
  const ff = await loadFFmpeg();

  let progressHandler;
  if (onProgress) {
    progressHandler = ({ progress }) => {
      onProgress(Math.min(99, Math.round(progress * 100)));
    };
    ff.on('progress', progressHandler);
  }

  try {
    const inputData = await fetchFile(blobUrl);
    await ff.writeFile('input.mp4', inputData);

    // -vn 去掉视频流，-acodec copy 直接复制音频流（零重编码）
    await ff.exec([
      '-i', 'input.mp4',
      '-vn',
      '-acodec', 'copy',
      '-y',
      'output.m4a'
    ]);

    const outputData = await ff.readFile('output.m4a');

    // 检查输出是否有效（视频可能没有音频轨道）
    if (!outputData || outputData.length < 100) {
      throw new Error('该视频不包含音频轨道，无法提取');
    }

    try { await ff.deleteFile('input.mp4'); } catch {}
    try { await ff.deleteFile('output.m4a'); } catch {}

    if (onProgress) onProgress(100);
    resetIdleTimer();

    return new Blob([outputData.buffer], { type: 'audio/mp4' });
  } finally {
    if (progressHandler) {
      ff.off('progress', progressHandler);
    }
  }
}
